import { compareVersions } from 'compare-versions';
import React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { releaseVersion } from '@gitbook/env';
import { Button, toast } from '@gitbook/spine-react';
import { localStorage } from '@gitbook/util-local-storage';
import { useIsMounted } from '@gitbook/util-react-hooks';

import { apiClient, calculateDaysDifference, usePageVisibility } from '../../state';

const UPDATE_THRESHOLD = 7; // days
/**
 * Toast component to notify the user that a new version of the app is available.
 */
export function AppVersionToast() {
    const visible = usePageVisibility();
    const showToast = useAppVersionToast();
    const dismissRef = React.useRef<(() => void) | null>(null);
    React.useEffect(() => {
        if (visible) {
            // Show the toast when the page becomes visible
            dismissRef.current = showToast();
        }
        return () => {
            // Dismiss the toast when the component is unmounted or page becomes hidden
            dismissRef.current?.();
        };
    }, [visible, showToast]);
    return null;
}

/**
 * Hook to trigger a toast notification when there is an app version update.
 */
function useAppVersionToast() {
    const isMounted = useIsMounted();
    // Return a function that checks for updates and shows a toast if needed.
    return React.useCallback(() => {
        if (!isMounted) {
            return () => {};
        }
        const lastUpdated = localStorage.get<[string, string]>('app_version_updated');
        // If the app has already been updated recently, do not show the toast again for a few days.
        if (lastUpdated) {
            const [lastVersion, lastDate] = lastUpdated;
            if (
                compareVersions(releaseVersion, lastVersion) === 0 ||
                calculateDaysDifference(new Date(lastDate), new Date()) < UPDATE_THRESHOLD
            ) {
                return () => {};
            }
        }

        let toastId: string | number | null = null;
        // not using async/await because we want to return the cleanup function synchronously
        apiClient.system.getApiInformation().then(({ data: apiInfo }) => {
            /* compare versions, -1 means the releaseVersion is less than the current version in api info */
            if (compareVersions(releaseVersion, apiInfo.version) === -1) {
                toastId = toast.info('New version available.', {
                    persist: true,
                    description:
                        'A new improved version of GitBook is available. Please reload to update.',
                    closeButton: false,
                    action: (
                        <Button
                            kind="secondary"
                            size="small"
                            label="Reload"
                            onPress={() => {
                                analytics.logEvent({
                                    type: 'app_version_update',
                                    previousVersion: releaseVersion,
                                    currentVersion: apiInfo.version,
                                });
                                localStorage.set('app_version_updated', [
                                    apiInfo.version,
                                    new Date().toISOString(),
                                ]);
                                window.location.reload();
                            }}
                        />
                    ),
                });
            }
        });
        // returns a cleanup function to dismiss the toast when the component is unmounted
        // the version comparison is async and we only get a toastId if that comparison determines we need an update.
        return () => {
            toastId ? toast.dismiss(toastId) : undefined;
        };
    }, [isMounted]);
}
